import { useCallback, useEffect, useRef, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";

import { type AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";
import { cacheScope, readCache } from "@/lib/ui-cache";
import { useCacheScope } from "@/hooks/use-cache-scope";
import type { UnifiedPreferencesSnapshot } from "@/lib/unified-preferences";
import {
  patchUnifiedPreferences,
  readUnifiedPreferences,
} from "@/lib/unified-preferences";

/** Saved sidebar display order for pinned agents and projects. */
export interface SidebarPinOrder {
  agentIds: string[];
  projectIds: string[];
}

const EMPTY_ORDER: SidebarPinOrder = { agentIds: [], projectIds: [] };

export function emptySidebarPinOrder(): SidebarPinOrder {
  return { ...EMPTY_ORDER, agentIds: [], projectIds: [] };
}

function toOrderedIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !item || seen.has(item)) continue;
    seen.add(item);
    result.push(item);
  }
  return result;
}

export interface UseSidebarPinOrderReturn {
  /** Raw saved order, or null until the first successful read confirms none. */
  order: SidebarPinOrder | null;
  /** True after at least one successful preference GET. */
  loaded: boolean;
  /** Read-specific failure used to decide whether reordering may persist. */
  loadError: string | null;
  /** Failure of the most recent save, surfaced as a non-blocking notice. */
  saveError: string | null;
  /** Persists a complete order snapshot. Resolves false when unsupported/failed. */
  save: (next: SidebarPinOrder) => Promise<boolean>;
}

/**
 * Server-backed sidebar pin display order. A failed save (for example against
 * an older bridge without this preference) is reported through `saveError` so
 * callers can keep the session-local order and degrade gracefully instead of
 * breaking pin/unpin or rendering nothing.
 */
export function useSidebarPinOrder(
  getToken: AccessTokenGetter | null,
): UseSidebarPinOrderReturn {
  const verifiedScope = useCacheScope();
  const [order, setOrder] = useState<SidebarPinOrder | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  // A preference GET can be in flight while the user drags a row.  Its
  // response is a snapshot from before the PUT, so never let it overwrite a
  // newer local write when it eventually arrives.
  const writeGenerationRef = useRef(0);
  const pendingWritesRef = useRef(0);
  const backendReconnectKey = useBackendReconnectKey();
  const liveRef = useRef(false);

  useEffect(() => {
    // SessionSidebar accepts a missing token getter (tests, narrow embeds).
    // Without auth there is nothing to read; ordering then stays session-local.
    if (!getToken) {
      setOrder((current) => current ?? { agentIds: [], projectIds: [] });
      setLoaded(true);
      return;
    }
    liveRef.current = false;
    setOrder(null);
    let cancelled = false;
    if (verifiedScope) void readCache<UnifiedPreferencesSnapshot>("displayPrefs", "display", "settings").then((snapshot) => {
      if (cancelled || liveRef.current || cacheScope() !== verifiedScope || !snapshot) return;
      setOrder({ agentIds: toOrderedIds(snapshot.sidebarPinOrder.agentIds), projectIds: toOrderedIds(snapshot.sidebarPinOrder.projectIds) });
    });
    const writeGenerationAtStart = writeGenerationRef.current;
    const writeWasPendingAtStart = pendingWritesRef.current > 0;
    const request = (async () => {
      try {
        const snapshot = await readUnifiedPreferences(getToken);
        const next = {
          agentIds: toOrderedIds(snapshot.sidebarPinOrder.agentIds),
          projectIds: toOrderedIds(snapshot.sidebarPinOrder.projectIds),
        };
        // A read that began before a write (or while one was outstanding) is
        // not authoritative for this page session. This also covers a
        // response that was held by the browser, proxy, or network after the
        // server had already read the old document.
        if (
          cancelled
          || writeWasPendingAtStart
          || writeGenerationAtStart !== writeGenerationRef.current
        ) return;
        setOrder(next);
        setLoaded(true);
        liveRef.current = true;
        setLoadError(null);
      } catch (err) {
        if (
          cancelled
          || writeWasPendingAtStart
          || writeGenerationAtStart !== writeGenerationRef.current
        ) return;
        // No saved order yet (404 on older bridges) behaves like an empty one:
        // default ordering still renders, only persistence is unavailable.
        setLoadError(err instanceof Error ? err.message : i18n.t("sidebar.errors.loadPinOrder"));
        setOrder((current) => current ?? { agentIds: [], projectIds: [] });
        setLoaded(true);
      }
    })();
    inFlightRef.current = request;
    void request.finally(() => {
      if (inFlightRef.current === request) inFlightRef.current = null;
    });
    return () => {
      cancelled = true;
    };
  }, [backendReconnectKey, getToken, verifiedScope]);

  const save = useCallback(async (next: SidebarPinOrder): Promise<boolean> => {
    if (!getToken) {
      // No auth context (tests, narrow embeds): keep session-local ordering.
      setSaveError("unauthenticated");
      return false;
    }
    const writeGeneration = writeGenerationRef.current + 1;
    writeGenerationRef.current = writeGeneration;
    pendingWritesRef.current += 1;
    setSaveError(null);
    try {
      await patchUnifiedPreferences(getToken, { sidebarPinOrder: next });
      // Validate the response, but keep the optimistic desired snapshot as
      // the display source of truth after a successful write. A bridge can
      // persist the PUT and still return an empty/older snapshot (for example
      // while an independently deployed server is rolling out); applying that
      // response would visibly undo a drag even though the saved order is
      // already correct. A later authoritative GET/reload can reconcile the
      // state without making the current page jump backwards.
      // A later drop owns the display state. Its PUT includes the latest
      // combined snapshot, so an earlier response must not put an interim
      // order back on screen.
      if (writeGeneration !== writeGenerationRef.current) return true;
      setSaveError(null);
      return true;
    } catch (err) {
      if (writeGeneration !== writeGenerationRef.current) return false;
      setSaveError(err instanceof Error ? err.message : i18n.t("sidebar.errors.savePinOrder"));
      return false;
    } finally {
      pendingWritesRef.current -= 1;
    }
  }, [getToken]);

  return { order, loaded, loadError, saveError, save };
}
